import Cookies from 'js-cookie';
import articleConfig from '~/config/article/';
import { safeLocalStorageGet, safeLocalStorageSetDelete } from '~/utils/localStorage';
import Session from './Session';

/**
 * Class contains methods to work with token cookie.
 * Mainly it's to save/delete token, and to manage user api key, login method and other stuff in cookies
 */
class TokenHandler {
  /** Check if anonymous commenting is enabled */
  private anonCommenting: boolean = false;
  public defaultCookieOptions: Cookies.CookieAttributes = { expires: 30, sameSite: 'Lax' };

  /**
   * Detect if we need to use token that will be only used for one publisher.
   * If all auth methods are disabled then we use SSO feature, this means that token should be unique for publisher
   * and authorization from other sites should not work here.
   */
  private useIsolatedToken: boolean = false;

  /**
   * For guest commenting (not guest + anonymous)
   * We use a separate token called 'guest_token'
   */
  public guestCommenting: boolean = false;

  /**
   * Use regular or SSO session ('apikey-token' or 'token')
   */
  public regularCommenting: boolean = true;

  /**
   * Change listeners for tokens
   */
  public ssoTokenChangeListeners: Map<string, (token: string) => any> = new Map();

  /**
   * Logout listeners for SSO
   */
  public ssoLogoutListeners: Map<string, () => any> = new Map();

  /** Check if SSO or Regular token is present */
  public isRegularTokenPresent(): boolean {
    const regularTokenName = `vuukle_${this.useIsolatedToken ? articleConfig.apiKey + '-' : ''}token`;
    return articleConfig.amp
      ? safeLocalStorageGet(regularTokenName) !== null
      : Cookies.get(regularTokenName) !== undefined;
  }

  public isAnonymousTokenPresent(): boolean {
    return articleConfig.amp
      ? safeLocalStorageGet('vuukle_anonymous_token') !== null
      : Cookies.get('vuukle_anonymous_token') !== undefined;
  }

  public addSSOTokenListener(id: string, listener: (token: string) => void) {
    this.ssoTokenChangeListeners.set(id, listener);
  }

  public addSSOLogoutListener(id: string, listener: () => void) {
    this.ssoLogoutListeners.set(id, listener);
  }

  public fireTokenListeners(token?: string) {
    this.ssoTokenChangeListeners.forEach((listener) => listener(token || this.getToken() || ''));
  }

  public fireSSOLogoutListeners() {
    this.ssoLogoutListeners.forEach((listener) => listener());
  }

  /**
   * For SSO auth, we need to use unique identifiers like publisher API key to generate a unique token name,
   * so that it can be isolated from other cookies.
   * For Anonymous commenting we will use 'anonymous_token'
   * For Guest commenting we will use 'guest_token'
   * For SSO we use 'apiKey-token' combination
   * For Regular commenting we will use the environment variable or 'token'.
   * Regular token will be preferred over guest and anonymous tokens if present
   */
  public get tokenName(): string {
    const regularOrSSOToken: boolean = this.isRegularTokenPresent();

    if (regularOrSSOToken || this.regularCommenting) {
      return `vuukle_${this.useIsolatedToken ? articleConfig.apiKey + '-' : ''}token`;
    } else if (this.guestCommenting && !this.anonCommenting) {
      return 'vuukle_guest_token';
    } else {
      return 'vuukle_anonymous_token';
    }
  }

  public get isSSO(): boolean {
    return this.useIsolatedToken;
  }

  /**
   * Set the useIsolatedToken variable.
   * @param useIsolatedToken Determine if isolated tokens should be used or not
   */
  public setSSOInfo(useIsolatedToken: boolean) {
    this.useIsolatedToken = useIsolatedToken;
  }

  /**
   * Set the guestCommenting variable.
   * @param guestCommenting Determine if guest tokens should be used or not
   */
  public setGuestCommenting(guestCommenting: boolean) {
    this.guestCommenting = guestCommenting;
  }

  /**
   * Set the regularCommenting variable.
   * @param regularCommenting Determine if user session is regular
   */
  public setRegularCommenting(regularCommenting: boolean) {
    this.regularCommenting = regularCommenting;
  }

  /**
   * Set the anonymous commenting variable
   * @param anonymousCommenting Determine if the user session is anonymous
   */
  public setAnonymousCommenting(anonymousCommenting: boolean) {
    this.anonCommenting = anonymousCommenting;
  }

  /**
   * Saves token in cookies based on value
   * @param {string} token - token value to save
   */
  public saveToken(token: string) {
    if (Session.canUseCookies) {
      import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet(
          this.useIsolatedToken ? 'vuukle_token' : this.tokenName,
          () => {
            articleConfig.amp
              ? safeLocalStorageSetDelete(this.tokenName, token)
              : Cookies.set(this.tokenName, token, this.defaultCookieOptions);
          }
        );
      });
    } else {
      Session.addStorageUpdateCallback(() => {
        import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
          consentManager.default.executeWhenStorageValueCanBeSet(
            this.useIsolatedToken ? 'vuukle_token' : this.tokenName,
            () => {
              articleConfig.amp
                ? safeLocalStorageSetDelete(this.tokenName, token)
                : Cookies.set(this.tokenName, token, this.defaultCookieOptions);
            }
          );
        });
      });
    }
  }

  /**
   * Saves guest token in cookies
   * @param {string} token - token value to save
   */
  public setGuestToken(token: string) {
    if (Session.canUseCookies) {
      import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_guest_token', () => {
          articleConfig.amp
            ? safeLocalStorageSetDelete('vuukle_guest_token', token)
            : Cookies.set('vuukle_guest_token', token, this.defaultCookieOptions);
        });
      });
    } else {
      Session.addStorageUpdateCallback(() => {
        import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
          consentManager.default.executeWhenStorageValueCanBeSet('vuukle_guest_token', () => {
            articleConfig.amp
              ? safeLocalStorageSetDelete('vuukle_guest_token', token)
              : Cookies.set('vuukle_guest_token', token, this.defaultCookieOptions);
          });
        });
      });
    }
  }

  /** Get anonymous token */
  public getAnonToken() {
    return articleConfig.amp
      ? safeLocalStorageGet('vuukle_anonymous_token') || ''
      : Cookies.get('vuukle_anonymous_token') || '';
  }

  /**
   * Set non-cross-site anon user token or remove it
   * @param token
   */
  public setAnonToken(token: string | null) {
    if (!token) {
      articleConfig.amp
        ? safeLocalStorageSetDelete('vuukle_anonymous_token', null)
        : Cookies.remove('vuukle_anonymous_token', Session.sameSiteCookieOptions);
    }

    if (!Session.canUseCookies) {
      if (token) {
        Session.addStorageUpdateCallback(() => {
          import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
            consentManager.default.executeWhenStorageValueCanBeSet('vuukle_anonymous_token', () => {
              articleConfig.amp
                ? safeLocalStorageSetDelete('vuukle_anonymous_token', token)
                : Cookies.set('vuukle_anonymous_token', token, Session.sameSiteCookieOptions);
            });
          });
        });
      }
      return;
    }

    if (token) {
      import(/* webpackChunkName: "cmg" */ './ConsentManager').then((consentManager) => {
        consentManager.default.executeWhenStorageValueCanBeSet('vuukle_anonymous_token', () => {
          articleConfig.amp
            ? safeLocalStorageSetDelete('vuukle_anonymous_token', token)
            : Cookies.set('vuukle_anonymous_token', token, Session.sameSiteCookieOptions);
        });
      });
    }
  }

  /**
   * Removes token from cookies
   * @returns {void}
   */
  public removeToken(): void {
    articleConfig.amp
      ? safeLocalStorageSetDelete(this.tokenName, null)
      : Cookies.remove(this.tokenName, this.defaultCookieOptions);
  }

  /**
   * Returns token from cookies if exists otherwise undefined
   * @returns {string | undefined} token value or undefined if not found
   */
  public getToken(): string | undefined {
    return articleConfig.amp ? safeLocalStorageGet(this.tokenName) || undefined : Cookies.get(this.tokenName);
  }
}

export default new TokenHandler();
